import React, { useState } from 'react';

import { SelectChangeEvent } from '@mui/material';
import { Coupon } from 'common/contracts';
import { FormikErrors } from 'formik';
import { FormikTouched } from 'formik';
import { capitalizeFirstLetter } from 'utils/formattingUtil';

import AcSelect from 'components/AcSelect/AcSelect';
import RadioButtonGroup from 'components/RadioButton/RadioButtonGroup';

interface GeneralSettingsProps {
  handleChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleBlur: (e: React.FocusEvent<HTMLInputElement>) => void;
  errors: FormikErrors<Coupon>;
  touched: FormikTouched<Coupon>;
  setFieldValue: (field: string, value: any) => void;
  getOffersForSelect: (supportedOffersByType?: string[]) => any[];
  supportedOffersBySku: string[];
}

const promoCodeTypes = ['promotions', 'bundles', 'popups'];

export default function CouponsOffersSettings({
  handleBlur,
  errors,
  touched,
  setFieldValue,
  getOffersForSelect,
  supportedOffersBySku
}: GeneralSettingsProps) {
  const [selectedOfferTypes, setSelectedOfferTypes] =
    useState<string[]>(promoCodeTypes);

  const [offerSelectionMode, setOfferSelectionMode] = useState<
    'select' | 'all'
  >(supportedOffersBySku && supportedOffersBySku.length > 0 ? 'select' : 'all');

  const handleOfferSelectionModeChange = (value: string) => {
    const mode = value as 'select' | 'all';
    setOfferSelectionMode(mode);
    if (mode === 'all') {
      setSelectedOfferTypes([]);
      setFieldValue('supportedOffersBySku', []);
    }
  };

  return (
    <div className="flex flex-col gap-[33px]">
      <RadioButtonGroup
        dataTestId="offerSelectionMode"
        className="[&_button]:border [&_button]:border-base-border [&_button]:h-4 [&_button]:w-4 [&_svg]:!h-2 [&_svg]:!w-2"
        value={offerSelectionMode}
        onValueChange={handleOfferSelectionModeChange}
        options={[
          {
            value: 'all',
            label: 'All Offers'
          },
          {
            value: 'select',
            label: 'Select Offers'
          }
        ]}
      />
      {offerSelectionMode === 'select' && (
        <div className="flex gap-[18px]">
          {/* TODO: Task ACDEV-18482 - will be changed with reusable component from shared-ui */}
          <AcSelect
            header="Eligible Offer Types"
            sx={{ width: '323px' }}
            isSearchEnabled={true}
            isSelectAllEnabled
            name="supportedOffersByType"
            value={selectedOfferTypes}
            onChange={(e: SelectChangeEvent<unknown>) => {
              const newSelectedTypes = e.target.value as string[];
              setSelectedOfferTypes(newSelectedTypes);
              setFieldValue(
                'supportedOffersBySku',
                getOffersForSelect(newSelectedTypes).map((offer) => offer.key)
              );
            }}
            onBlur={handleBlur}
            isCheckBox
            items={promoCodeTypes.map((type) => ({
              content: capitalizeFirstLetter(type),
              key: type,
              value: type
            }))}
            setSelectedItems={(selected: any) => {
              setSelectedOfferTypes(selected);
              setFieldValue(
                'supportedOffersBySku',
                getOffersForSelect(selected as any[]).map(
                  (offers) => offers.key
                )
              );
            }}
            renderValue={(selected: any) => {
              if (selected.length === 3) return 'All offer types';
              return selected
                ?.map((item: string) => {
                  return item?.charAt(0)?.toUpperCase() + item?.slice(1);
                })
                .join(', ');
            }}
            dataTestBoxId="eligible-offer-types-dropdown"
            dataTestListId="eligible-offer-types-list"
          />
          {/* TODO: Task ACDEV-18482 - will be changed with reusable component from shared-ui */}
          <AcSelect
            isSearchEnabled={true}
            header="Offers"
            sx={{ width: '323px' }}
            isSelectAllEnabled
            name="supportedOffersBySku"
            value={supportedOffersBySku}
            onChange={(e: SelectChangeEvent<unknown>) => {
              setFieldValue(
                'supportedOffersBySku',
                e.target.value as string[]
              );
            }}
            onBlur={handleBlur}
            isCheckBox
            items={getOffersForSelect(selectedOfferTypes)}
            setSelectedItems={(selected: any) => {
              const selectedOfferTypesKeys = getOffersForSelect(
                selectedOfferTypes
              )
                .map((item: any) =>
                  selected.includes(item.value) ? item.key : null
                )
                .filter(Boolean);

              setFieldValue('supportedOffersBySku', selectedOfferTypesKeys);
            }}
            renderValue={(selected: any) => {
              if (selected.length === 0) return '';
              if (
                selected.length ===
                getOffersForSelect(selectedOfferTypes).length
              )
                return `All ${selectedOfferTypes.join(',')}`;
              return selected.length > 2
                ? `${selected.length} Offers selected`
                : selected
                    ?.map((item: string) => {
                      return item?.charAt(0)?.toUpperCase() + item?.slice(1);
                    })
                    .join(', ');
            }}
            error={
              touched.supportedOffersBySku &&
              Boolean(errors.supportedOffersBySku)
            }
            helperText={
              touched.supportedOffersBySku
                ? errors.supportedOffersBySku?.toString()
                : ''
            }
            dataTestListId="select-offers-list"
            dataTestBoxId="select-offers-dropdown"
          />
        </div>
      )}
    </div>
  );
}
